import React, { useState, useEffect, useCallback } from "react";
import { withRouter } from "react-router-dom";
import Table from "components/common/Table/table";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import LinearProgress from "@material-ui/core/LinearProgress";
import EnhancedTableToolbar from "components/common/Table/tableToolbar";
import ToolbarSelected from "components/common/Table/tableToolbarSelected";
import ToolbarDefault from "components/common/Table/tableToolbarDefault";
import ContactsDelete from "./dialog/contactsDelete";
import FieldName from "./fieldName";
import FieldAbout from "./fieldAbout";
import FieldEmail from "./fieldEmail";
import ContactsTableActions from "./contactsTableActions";
import ContactsListActions from "./contactsListActions";
import FiltersAppliedList from "components/common/Filters/filtersAppliedList";
import { getCustomerContact } from "services/customerService";
import useStyles from "./styles";

import { useTableState, useTableDispatch } from "contexts/tableContext";
import { useListState, useListDispatch } from "contexts/listContext";
import { useFilterState, useFilterDispatch } from "contexts/filterContext";
import { useCustomerView } from "contexts/customerViewContext";
import LoadingStateHorizontal from "components/common/LoadingStateHorizontal/LoadingStateHorizontal";

const columns = [
  {
    id: "id",
    label: "Name",
    checkbox: {
      active: (row) => false,
    },
    content: (row) => <FieldName row={row} />,
  },
  {
    id: "email",
    label: "Email",
    content: (row) => <FieldEmail field={row.email} />,
  },
  {
    id: "phone_number",
    label: "Phone",
  },
  {
    id: "title",
    label: "Title",
  },
  {
    id: "about",
    label: "About",
    content: (row) => <FieldAbout field={row?.about} fieldLength={20} />,
  },
  {
    id: "type",
    label: "Type",
    content: (row) => (row.type === 1 ? "Active User" : "General"),
  },
  {
    id: "actions",
    content: (row) => <ContactsTableActions row={row} />,
    contentMobile: (row) => <ContactsTableActions row={row} mobile={true} />,
    style: { width: 80, textAlign: "right" },
  },
];
const SCROLL_PAGINATION = 100;

const ContactsTable = (props) => {
  const classes = useStyles();
  const [tablePointer, setTablePointer] = useState(SCROLL_PAGINATION);
  const [loadingMore, setLoadingMore] = useState(false);
  const { listLoading, list, count, dummyRefresh } = useListState();
  const { hasRowsClickable, selected, rows } = useTableState();
  const { searchString, filters } = useFilterState();
  const dispatchTable = useTableDispatch();
  const dispatchList = useListDispatch();
  const dispatchFilters = useFilterDispatch();
  const [stateContext] = useCustomerView();
  const { customer } = stateContext ?? null;

  useEffect(() => {
    const fetchData = async () => {
      const result = await getCustomerContact(customer.id);

      const contactResult = result.data.data.contacts.map((contact) => {
        if (contact?.role_id) {
          return {
            ...contact,
            type: 1,
            phone_number: contact.phone,
            title: contact.job_title,
            about: contact?.profile?.note,
            photo: contact?.profile?.picture?.file_url,
          };
        }
        return {
          ...contact,
        };
      });

      contactResult.sort((a, b) => {
        if (a.first_name < b.first_name) {
          return -1;
        }
        if (a.first_name > b.first_name) {
          return 1;
        }
        return 0;
      });
      dispatchList({
        type: "SET_LIST",
        list: contactResult,
      });
      dispatchList({
        type: "SET_COUNT",
        count: contactResult.length,
      });
      dispatchTable({
        type: "UPDATE_ROWS",
        rows: contactResult.slice(0, tablePointer),
      });
      dispatchTable({ type: "UPDATE_COLUMNS", columns: columns });
      return dispatchList({
        type: "SET_LOADING",
        listLoading: false,
      });
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchList, dummyRefresh]);

  useEffect(() => {
    if (!listLoading) {
      dispatchTable({
        type: "UPDATE_ROWS",
        rows: list.slice(0, tablePointer),
      });
      dispatchTable({ type: "UPDATE_COLUMNS", columns: columns });
      setTimeout(() => {
        setLoadingMore(false);
      }, 1000);
    }
  }, [dispatchTable, list, listLoading, tablePointer]);

  const handleScrollClick = useCallback(async () => {
    if (searchString || list.length <= tablePointer) {
      // If search activated not scroll
      return;
    }

    try {
      setLoadingMore(true);
      const updateTablePointer = tablePointer + SCROLL_PAGINATION;
      setTablePointer(updateTablePointer);
      setLoadingMore(false);
    } catch (e) {
      console.log("Cannot load more data", e);
    }
  }, [tablePointer, list.length, searchString]);

  const handleRowClick = (event, row) => {
    if (hasRowsClickable) {
      console.log("Click on row event");
    }
    return;
  };

  const handleSearch = (ev, query) => {
    if (query) {
      if (ev.key === "Enter" || ev.type === "click") {
        const queryNormalize = query.toLowerCase().replace(/^\s+/g, "");
        const resultSearch = list.filter(
          (contactItem) =>
            contactItem?.first_name.toLowerCase().includes(queryNormalize) ||
            contactItem?.last_name.toLowerCase().includes(queryNormalize) ||
            contactItem?.title?.toLowerCase().includes(queryNormalize) ||
            contactItem?.about?.toLowerCase().includes(queryNormalize)
        );
        dispatchTable({
          type: "UPDATE_ROWS",
          rows: resultSearch,
        });
        dispatchFilters({
          type: "SET_SEARCH_STRING",
          searchString: query,
        });
        ev.preventDefault();
      }
    }
    return;
  };

  const handleSearchClear = () => {
    dispatchFilters({
      type: "SET_SEARCH_STRING",
      searchString: "",
    });
    dispatchTable({
      type: "UPDATE_ROWS",
      rows: list.slice(0, tablePointer),
    });
  };

  return (
    <Box className={classes.root}>
      {listLoading ? (
        <LoadingStateHorizontal isVisible style={classes.centerLoading} />
      ) : (
        <>
          <Box className={classes.contentContainer}>
            <Box className={classes.contentCounter}>
              {!searchString ? (
                <Chip
                  label={`${
                    count > tablePointer ? tablePointer : count
                  } / ${count}`}
                  color="secondary"
                  variant="outlined"
                />
              ) : (
                <Chip
                  label={rows.length}
                  color="secondary"
                  variant="outlined"
                />
              )}
            </Box>
            <Box className={classes.rootContainer}>
              <Box className={classes.tableContainer}>
                <Box className={classes.fullHeightTable}>
                  <Table
                    handleRowClick={handleRowClick}
                    newColumns={columns}
                    handleScrollClick={handleScrollClick}
                    activeMobile={true}
                  >
                    <EnhancedTableToolbar>
                      {selected.length > 0 ? (
                        <ToolbarSelected />
                      ) : (
                        <ToolbarDefault
                          handleSearch={handleSearch}
                          handleSearchClear={handleSearchClear}
                          placeholderSearch={"Customer Contacts"}
                        >
                          {<ContactsListActions />}
                        </ToolbarDefault>
                      )}
                    </EnhancedTableToolbar>
                    {filters ? <FiltersAppliedList /> : null}
                  </Table>
                </Box>
              </Box>
            </Box>
            {loadingMore ? (
              <Box className={classes.loadingTable}>
                <LinearProgress color="secondary" />
              </Box>
            ) : null}
          </Box>
        </>
      )}
      <ContactsDelete />
    </Box>
  );
};

export default withRouter(ContactsTable);
